import { useState } from 'react';

const Products = () => {
  const [selectedProduct, setSelectedProduct] = useState<number | null>(null);

  const products = [
    {
      category: 'Equipment',
      title: 'Deep Hole Drilling Machines',
      description: 'High-precision CNC drilling systems designed for complex manufacturing applications with exceptional accuracy and reliability.',
      features: ['CNC Control', 'Auto Calibration', 'Real-time Monitoring'],
      image: 'https://www.hi-tak.co.jp/wp-content/uploads/2017/01/kougu01.jpg',
      detailedInfo: {
        specifications: [
          'Maximum drilling depth: 3000mm',
          'Drilling diameter range: φ0.5~30mm',
          'Positioning accuracy: ±0.005mm',
          'Spindle speed: 50-3000 rpm'
        ],
        applications: [
          'Hydraulic cylinder manufacturing',
          'Mold cooling channels',
          'Aerospace components',
          'Medical device production'
        ],
        benefits: [
          'Reduced production time by 40%',
          'Enhanced precision and repeatability',
          'Lower operational costs',
          'Minimal maintenance requirements'
        ]
      }
    },
    {
      category: 'Technology',
      title: 'Honing Systems',
      description: 'Advanced honing technology delivering superior surface finish and dimensional accuracy for critical components across industries.',
      features: ['Mirror Finish', 'Precision Control', 'Multi-axis Operation'],
      image: 'https://www.hi-tak.co.jp/wp-content/uploads/2024/12/スクリーンショット-2024-12-26-082100.png',
      detailedInfo: {
        specifications: [
          'Surface roughness: Ra 0.05-0.2μm',
          'Bore diameter range: φ10-φ500mm',
          'Honing length: up to 2000mm',
          'Tolerance: ±0.002mm'
        ],
        applications: [
          'Engine cylinder bores',
          'Hydraulic components',
          'Bearing housings',
          'Precision tubes'
        ],
        benefits: [
          'Mirror-like surface finish',
          'Extended component lifespan',
          'Reduced friction and wear',
          'Improved sealing performance'
        ]
      }
    },
    {
      category: 'Service',
      title: 'Technical Support & Training',
      description: 'Comprehensive support services including installation, maintenance, operator training, and 24/7 technical assistance worldwide.',
      features: ['24/7 Support', 'On-site Training', 'Remote Diagnostics'],
      image: 'https://www.hi-tak.co.jp/wp-content/uploads/2017/02/gaikei.jpg',
      detailedInfo: {
        specifications: [
          'Global support network',
          'Multi-language assistance',
          'Response time: within 2 hours',
          'Certified technicians'
        ],
        applications: [
          'Equipment installation',
          'Preventive maintenance',
          'Operator certification programs',
          'Process optimization consulting'
        ],
        benefits: [
          'Maximized equipment uptime',
          'Reduced operational risks',
          'Enhanced operator skills',
          'Long-term cost savings'
        ]
      }
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-[#001E3C] to-[#002A4E] relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#00D9C9]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-[#0066FF]/5 rounded-full blur-3xl"></div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#00D9C9] text-sm font-semibold tracking-wider uppercase">Our Offerings</span>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mt-4 mb-6">
            Products & Services
          </h2>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            Comprehensive solutions from cutting-edge equipment to expert support services
          </p>
        </div>

        {/* Products Grid */}
        <div className="space-y-12">
          {products.map((product, index) => (
            <div
              key={index}
              className={`grid lg:grid-cols-2 gap-8 items-center ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              {/* Image */}
              <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="group relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#00D9C9]/20 to-[#0066FF]/20 rounded-xl blur-2xl group-hover:blur-3xl transition-all duration-500"></div>
                  <div className="relative rounded-xl overflow-hidden border border-white/10">
                    <div className="w-full h-[400px]">
                      <img
                        src={product.image}
                        alt={product.title}
                        className="w-full h-full object-cover object-top transform group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#001E3C] via-transparent to-transparent opacity-60"></div>
                    
                    {/* Category Badge */}
                    <div className="absolute top-6 left-6">
                      <span className="bg-[#00D9C9] text-[#001E3C] text-xs font-bold px-4 py-2 rounded-full uppercase tracking-wider">
                        {product.category}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                  {product.title}
                </h3>
                
                <p className="text-lg text-white/70 mb-6 leading-relaxed">
                  {product.description}
                </p>

                {/* Features */}
                <div className="grid grid-cols-3 gap-4 mb-8">
                  {product.features.map((feature, idx) => (
                    <div key={idx} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-4 text-center hover:bg-white/10 hover:border-[#00D9C9]/50 transition-all duration-300">
                      <div className="w-8 h-8 bg-[#00D9C9]/10 border border-[#00D9C9] rounded-full flex items-center justify-center mx-auto mb-2">
                        <i className="ri-check-line text-[#00D9C9] text-sm"></i>
                      </div>
                      <span className="text-white text-sm font-medium">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={() => setSelectedProduct(index)}
                    className="group bg-[#00D9C9] text-[#001E3C] px-8 py-3 rounded-lg font-bold hover:bg-[#00F5E0] transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    Learn More
                    <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform w-6 h-6 flex items-center justify-center"></i>
                  </button>
                  <a
                    href="#contact"
                    className="group bg-transparent text-white border-2 border-white/30 px-8 py-3 rounded-lg font-bold hover:border-[#00D9C9] hover:text-[#00D9C9] transition-all duration-300 flex items-center gap-2 whitespace-nowrap"
                  >
                    Request Quote
                    <i className="ri-mail-line text-xl w-6 h-6 flex items-center justify-center"></i>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Product Detail Modal */}
      {selectedProduct !== null && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedProduct(null)}
        >
          <div 
            className="bg-[#001E3C] border border-white/10 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-[#001E3C] border-b border-white/10 p-6 flex items-center justify-between z-10">
              <div>
                <span className="bg-[#00D9C9] text-[#001E3C] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                  {products[selectedProduct].category}
                </span>
                <h3 className="text-2xl lg:text-3xl font-bold text-white mt-3">
                  {products[selectedProduct].title}
                </h3>
              </div>
              <button
                onClick={() => setSelectedProduct(null)}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-8">
              {/* Image */}
              <div className="rounded-xl overflow-hidden border border-white/10">
                <div className="w-full h-[300px]">
                  <img
                    src={products[selectedProduct].image}
                    alt={products[selectedProduct].title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <p className="text-lg text-white/70 leading-relaxed">
                  {products[selectedProduct].description}
                </p>
              </div>

              {/* Specifications */}
              <div>
                <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <i className="ri-settings-3-line text-[#00D9C9]"></i>
                  Technical Specifications
                </h4>
                <div className="grid md:grid-cols-2 gap-3">
                  {products[selectedProduct].detailedInfo.specifications.map((spec, idx) => (
                    <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-4 flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#00D9C9] text-lg mt-0.5"></i>
                      <span className="text-white/80 text-sm">{spec}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Applications */}
              <div>
                <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <i className="ri-apps-line text-[#00D9C9]"></i>
                  Applications
                </h4>
                <div className="grid md:grid-cols-2 gap-3">
                  {products[selectedProduct].detailedInfo.applications.map((app, idx) => (
                    <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-4 flex items-start gap-3">
                      <i className="ri-arrow-right-s-line text-[#00D9C9] text-lg mt-0.5"></i>
                      <span className="text-white/80 text-sm">{app}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              <div>
                <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <i className="ri-star-line text-[#00D9C9]"></i>
                  Key Benefits
                </h4>
                <div className="grid md:grid-cols-2 gap-3">
                  {products[selectedProduct].detailedInfo.benefits.map((benefit, idx) => (
                    <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-4 flex items-start gap-3">
                      <i className="ri-check-double-line text-[#00D9C9] text-lg mt-0.5"></i>
                      <span className="text-white/80 text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 pt-4">
                <a
                  href="#contact"
                  onClick={() => setSelectedProduct(null)}
                  className="group bg-[#00D9C9] text-[#001E3C] px-8 py-3 rounded-lg font-bold hover:bg-[#00F5E0] transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  Request Quote
                  <i className="ri-mail-line text-xl w-6 h-6 flex items-center justify-center"></i>
                </a>
                <a
                  href="mailto:info@hi-tak.co.jp?subject=Inquiry from Hi-Tak Website"
                  className="group bg-transparent text-white border-2 border-white/30 px-8 py-3 rounded-lg font-bold hover:border-[#00D9C9] hover:text-[#00D9C9] transition-all duration-300 flex items-center gap-2 whitespace-nowrap"
                >
                  Contact Us
                  <i className="ri-external-link-line text-xl w-6 h-6 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Products;
